/**
 * Global state for the chat panel visibility and page context.
 *
 * Tracks whether the chat is open and its presentation mode ('panel' | 'fullscreen'),
 * and bridges the trusted session registrar down to nested route components.
 *
 * Context is derived from the current URL to give the agent awareness of what the user is viewing.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';

type ChatMode = 'panel' | 'fullscreen';

interface ChatPanelState {
  open: boolean;
  mode: ChatMode;
  context: string | null;
  registerLocalSession: (sessionId: string) => void;
  setOpen: (open: boolean) => void;
  setMode: (mode: ChatMode) => void;
}

export function deriveContext (pathname: string, search = ''): string | null {
  const campaignMatch = pathname.match(/^\/campaigns\/([a-f0-9]{24})$/);
  if (campaignMatch) {
    return `The user is viewing campaign ${campaignMatch[1]}.`;
  }
  if (pathname === '/campaigns') return 'The user is on the campaigns list page.';
  if (pathname.startsWith('/blocklists/')) return `The user is viewing blocklist ID: ${pathname.split('/')[2]}.`;
  return null;
}

export interface EntityContext {
  entityId: string;
  entityType: string;
}

/** Derives the entity association for agent-sessions tracking from the current URL. */
export function deriveEntity (pathname: string): EntityContext | null {
  const campaignMatch = pathname.match(/^\/campaigns\/([a-f0-9]{24})$/);
  if (campaignMatch) return { entityId: campaignMatch[1], entityType: 'campaign' };
  return null;
}

const registerLocalSessionOutsideRoot = () => {};

const ChatPanelContext = createContext<ChatPanelState>({
  open: false,
  mode: 'panel',
  context: null,
  registerLocalSession: registerLocalSessionOutsideRoot,
  setOpen: () => {},
  setMode: () => {},
});

export const ChatPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ChatMode>('panel');

  return (
    <ChatPanelContext.Provider value={{ open, mode, context: null, registerLocalSession: registerLocalSessionOutsideRoot, setOpen, setMode }}>
      {children}
    </ChatPanelContext.Provider>
  );
};

interface ChatPanelSessionRegistrationProviderProps {
  readonly children: React.ReactNode;
  readonly registerLocalSession: (sessionId: string) => void;
}

/** Makes RootSideSheet's trusted-session registrar available to nested route components. */
export const ChatPanelSessionRegistrationProvider: React.FC<ChatPanelSessionRegistrationProviderProps> = ({
  children,
  registerLocalSession,
}) => {
  const panelState = useContext(ChatPanelContext);
  const value = useMemo(
    () => ({ ...panelState, registerLocalSession }),
    [panelState, registerLocalSession],
  );

  return <ChatPanelContext.Provider value={value}>{children}</ChatPanelContext.Provider>;
};

export const useChatPanel = (): ChatPanelState => useContext(ChatPanelContext);
